import { InformationCircleIcon } from '@heroicons/react/24/outline';
import Modal from 'antd/es/modal/Modal';
import Tooltip from 'antd/es/tooltip';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useOrganisationId } from 'hooks/context';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';
import { DetailsDraftAttachmentSectionContainer } from '@atoms/DetailsAttachmentSection/DetailsDraftAttachmentSectionContainer';

import { CheckCircleIcon } from 'components/ui/atoms/Icon';

import {
  PAYOUT_NEEDS_APPROVAL,
  SUCCESSFULLY_CREATED,
  VIEW_REQUESTS,
  FINISH_AND_CLOSE
} from '../constants';

type Props = {
  onClose: () => void;
  amount: string;
  currency: string;
  reference: string;
  draftId: number;
};

export const DraftSentForApprovalDialog = ({
  onClose,
  amount,
  currency,
  reference,
  draftId
}: Props) => {
  const organisationId = useOrganisationId();
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    navigate(`/org/${organisationId}/payout_drafts`);
  }, [navigate, organisationId]);

  return (
    <Modal
      open
      onCancel={handleClick}
      footer={null}
      maskClosable={false}
      keyboard={false}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col items-center gap-4">
          <CheckCircleIcon className="mr-1 w-12 stroke-content-subtle" />
          <div className="text-lg font-bold">{SUCCESSFULLY_CREATED}</div>
          <div className="text-3xl font-bold">
            {currency} {amount}
          </div>
        </div>

        <div className="bg flex flex-col rounded-2xl border bg-surface-muted p-4">
          <div>
            End-to-end ID:
            <span className="ml-1 font-semibold">{reference}</span>
          </div>
        </div>
        <div className="rounded-2xl bg-status-action-required-active p-4">
          {PAYOUT_NEEDS_APPROVAL}
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center gap-1">
          <div className="text-sm font-medium">Attach files</div>
          <Tooltip
            title="This feature is not intended for RFIs"
            placement="right"
            arrow={true}
          >
            <InformationCircleIcon className="size-4 stroke-current" />
          </Tooltip>
        </div>
        <div className="pb-2 text-xs text-content-strong">
          Add supporting documents (optional).
        </div>
        <DetailsDraftAttachmentSectionContainer resourceId={draftId} />
      </div>

      <div className="flex w-full flex-col gap-4 py-3">
        <SecondaryButton className="w-full" size="large" onClick={handleClick}>
          {VIEW_REQUESTS}
        </SecondaryButton>
        <PrimaryButton className="w-full" size="large" onClick={onClose}>
          {FINISH_AND_CLOSE}
        </PrimaryButton>
      </div>
    </Modal>
  );
};
